import { Injectable } from '@angular/core'
import { RegionalApiClientFactory, RegionalApiClientSetupService } from '@ftr/api-regional'
import { TimeframeWithLocalTimes } from '@ftr/contracts/api/shared'
import { WeeklyStreamLocationConfiguration, getConfigForDayOfWeek } from '@ftr/contracts/regional-api'
import { DayOfWeek } from '@ftr/contracts/shared/DayOfWeek'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData, switchMapData } from '@ftr/foundation'
import { classToPlain } from '@ftr/serialization'
import { switchMap } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class WeeklyStreamLocationConfigurationService extends RegionalApiClientSetupService {
  override regionalApiPath = 'live-stream/location'

  constructor(regionalApiClientFactory: RegionalApiClientFactory) {
    super(regionalApiClientFactory)
  }

  saveDepartment(
    courtSystemId: Uuid,
    locationId: Uuid,
    departmentId: Uuid | undefined,
  ): ApiResult<WeeklyStreamLocationConfiguration> {
    return this.get(courtSystemId, locationId).pipe(
      switchMapData(current => {
        const currentValues = Object.values(current)
        currentValues.forEach(v => {
          v.departmentId = departmentId
        })
        return this.save(courtSystemId, locationId, current)
      }),
    )
  }

  save(
    courtSystemId: Uuid,
    locationId: Uuid,
    configuration: WeeklyStreamLocationConfiguration,
  ): ApiResult<WeeklyStreamLocationConfiguration> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.post({
          headers: { courtSystemId },
          path: `/${locationId}/weekly-configuration`,
          body: classToPlain(configuration),
        }),
      ),
      mapData(() => configuration),
    )
  }

  get(courtSystemId: Uuid, locationId: Uuid): ApiResult<WeeklyStreamLocationConfiguration> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.get<WeeklyStreamLocationConfiguration>({
          headers: { courtSystemId },
          path: `/${locationId}/weekly-configuration`,
          responseBodyType: WeeklyStreamLocationConfiguration,
        }),
      ),
    )
  }

  saveSchedule(
    courtSystemId: Uuid,
    locationId: Uuid,
    day: DayOfWeek,
    schedule: TimeframeWithLocalTimes | undefined,
    sessions: TimeframeWithLocalTimes[] = [],
  ): ApiResult<WeeklyStreamLocationConfiguration> {
    return this.get(courtSystemId, locationId).pipe(
      switchMapData(current => {
        const currentConfig = getConfigForDayOfWeek(current, day)
        currentConfig.schedule = schedule
        currentConfig.onTheRecordSchedules = sessions
        return this.save(courtSystemId, locationId, current)
      }),
    )
  }
}
